// api.js - Unified API Service
import axios from 'axios';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: 'https://welthandel-backend.vercel.app/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor to add auth headers
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const userEmail = localStorage.getItem('userEmail');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    if (userEmail) {
      config.headers.Email = userEmail;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('user');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => apiClient.post('/auth/login', credentials),
  register: (userData) => apiClient.post('/auth/register', userData),
  logout: () => apiClient.post('/auth/logout'),
  refreshToken: () => apiClient.post('/auth/refresh-token'),
  verifyEmail: (token) => apiClient.get(`/auth/verify-email/${token}`),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (data) => apiClient.post('/auth/reset-password', data),
};

// User API
export const userAPI = {
  getProfile: () => apiClient.get('/profile'),
  updateProfile: (userData) => apiClient.put('/profile', userData),
  updateKYC: (kycData) => apiClient.post('/profile/kyc', kycData),
  changePassword: (passwordData) => apiClient.put('/profile/password', passwordData),
  getSecuritySettings: () => apiClient.get('/profile/security'),
};

// Wallet API
export const walletAPI = {
  getWallet: () => apiClient.get('/wallet'),
  getBalance: () => apiClient.get('/wallet/balance'),
  getTransactions: (params = {}) => apiClient.get('/wallet/transactions', { params }),
  getTransaction: (id) => apiClient.get(`/wallet/transactions/${id}`),
};

// Deposit API
export const depositAPI = {
  createDeposit: (depositData) => apiClient.post('/deposit', depositData),
  getDeposits: (params = {}) => apiClient.get('/deposits', { params }),
  getDeposit: (id) => apiClient.get(`/deposits/${id}`),
  confirmDeposit: (confirmationData) => apiClient.post('/deposit/confirm', confirmationData),
  generateAddress: (coin) => apiClient.post('/deposit/generate-address', { coin }),
  getDepositHistory: () => apiClient.get('/deposits'),
};

// Withdrawal API
export const withdrawalAPI = {
  createWithdrawal: (withdrawalData) => apiClient.post('/withdraw', withdrawalData),
  getWithdrawals: (params = {}) => apiClient.get('/withdrawals', { params }),
  getWithdrawal: (id) => apiClient.get(`/withdrawals/${id}`),
  cancelWithdrawal: (id) => apiClient.post(`/withdrawals/${id}/cancel`),
  getWithdrawalHistory: () => apiClient.get('/withdrawal'),
  validateAddress: (addressData) => apiClient.post('/withdrawal/validate-address', addressData),
};

// Coin/Market API
export const marketAPI = {
  getCoinPrices: () => apiClient.get('/market/prices'),
  getCoinInfo: (symbol) => apiClient.get(`/market/coins/${symbol}`),
  getMarketData: () => apiClient.get('/market/data'),
  getSupportedCoins: () => apiClient.get('/market/supported-coins'),
};

// Investment/Package API
export const investmentAPI = {
  getPackages: () => apiClient.get('/packages'),
  invest: (investmentData) => apiClient.post('/invest', investmentData),
  getInvestments: () => apiClient.get('/investments'),
  getInvestment: (id) => apiClient.get(`/investments/${id}`),
};

// Utility functions
export const apiUtils = {
  handleAPIError: (error) => {
    if (error.response) {
      // Server responded with error status
      return {
        message: error.response.data?.message || 'An error occurred',
        status: error.response.status,
        data: error.response.data
      };
    } else if (error.request) {
      // Request made but no response received
      return {
        message: 'Network error. Please check your connection.',
        status: 0
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        status: -1
      };
    }
  },
  
  validateInput: (input) => {
    if (typeof input !== 'string') return '';
    // Remove script tags and trim
    return input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '').trim();
  },
  
  formatCurrency: (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  },
  
  formatDate: (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};

export default apiClient;